/**
 * Entrypoint: start the MSW mock API in development (there is no Django
 * backend to talk to for every screen yet, see `docs/FRONTEND.md`), then
 * mount React inside the router and the app-wide providers.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { ToastProvider } from './components/ui';
import './theme/tokens.css';
import './theme/ui.css';
import './styles/global.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Missing #root element in index.html');
}

/**
 * Start the MSW browser worker before the app mounts, in development only.
 * Production builds serve the real API from the same origin (see
 * `docs/DEPLOY.md`) and never import this module, so it is not part of the
 * `dist/` bundle.
 *
 * Set `VITE_USE_MSW=false` to work against a real Django on `:8001` (which
 * `vite.config.ts` proxies): the mocks are convenient for UI work without a
 * backend, but hide the actual API the rest of the time.
 *
 * @returns A promise that resolves once the mock worker is ready (or
 *   immediately, outside development or when mocking is turned off).
 */
async function enableMockingInDev(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_USE_MSW === 'false') {
    return;
  }
  const { worker } = await import('./mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

void enableMockingInDev().then(() => {
  createRoot(container).render(
    <StrictMode>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </StrictMode>,
  );
});
